import { useMemo } from "react";
import {
    getDebtListByPointIdThunk,
    getPointInfoCollectionThunk,
    getPointInventoryGetThunk,
    getTaskListsThunk,
    patchBuildingChangePhotoBase64,
    patchMarketSetShelfSize,
    patchPointInventorySet,
    postBuildingChangeGeo,
    postGeoAdd,
    postInvoiceMakePaymentByPoint,
    postOrderCreateByPoint,
    postOrderSave,
    postOrderSaveOne,
    postTaskAddDebtTaskThunk,
    postTaskAddDistributionPricesThunk,
    postTaskAddRejectTaskThunk,
    postTaskAddSaleTaskThunk,
    postTaskComplete,
} from "tm-shared/api";
import { extractBase64Content } from "tm-shared/utils";

import { ROLE_CHILDREN_MAP } from "@/constants/domain/managerAgentVocabulary";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { photoStorage } from "@/Services/PhotoStorage";
import { uploadPhoto } from "@/Services/UploadPhoto";
import { getCheckoutsThunk } from "@/store/slices/ui";
import {
    QueueCommand,
    QueueCommandMap,
    QueueCommandMethods,
    QueueCommandType,
} from "@/types/requestQueue";

import { isManagerRole } from "../../../utils/business/isManagerRole";

export const makeCommand = <T extends QueueCommandType>(
    cmd: Omit<QueueCommand<T>, "method">,
): QueueCommand<T> => ({
    ...cmd,
    method: QueueCommandMethods[cmd.type] ?? "custom function",
});

type CommandHandler<T extends QueueCommandType> = (
    params: QueueCommandMap[T],
) => Promise<unknown>;

type CommandHandlers = { [K in QueueCommandType]: CommandHandler<K> };

export const useRequestQueueCommands = (): CommandHandlers => {
    const dispatch = useAppDispatch();

    const role = useProtectedUserRoleSelector();
    const route = useAppSelector((s) => s.user.route);
    const points = useAppSelector((s) => s.work.points);
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);

    return useMemo(
        () =>
            ({
                postGeoAdd: async (p) => postGeoAdd(p),

                patchPointInventorySet: async (p) => patchPointInventorySet(p),
                postTaskComplete: async (p) => postTaskComplete(p),
                getPointInventoryGetThunk: async (p) =>
                    dispatch(getPointInventoryGetThunk(p)),

                postTaskAddSaleTaskThunk: async (p) =>
                    dispatch(postTaskAddSaleTaskThunk(p)).unwrap(),
                postOrderCreateByPoint: async (p) => postOrderCreateByPoint(p),
                postOrderSave: async (p) => postOrderSave(p),
                postBuildingChangeGeo: async (p) => postBuildingChangeGeo(p),
                postFileUpload: async (p) => uploadPhoto(p),
                postTaskAddDistributionPricesThunk: async (p) =>
                    dispatch(postTaskAddDistributionPricesThunk(p)).unwrap(),

                postTaskAddRejectTaskThunk: async (p) =>
                    dispatch(postTaskAddRejectTaskThunk(p)).unwrap(),
                postOrderSaveOne: async (p) => postOrderSaveOne(p),
                postTaskAddDebtTaskThunk: async (p) =>
                    dispatch(postTaskAddDebtTaskThunk(p)).unwrap(),
                patchMarketSetShelfSize: async (p) =>
                    patchMarketSetShelfSize(p),
                postInvoiceMakePaymentByPoint: async (p) =>
                    postInvoiceMakePaymentByPoint(p),
                getDebtListByPointIdThunk: async (p) =>
                    dispatch(getDebtListByPointIdThunk(p)).unwrap(),
                getPointInfoCollectionWorkThunk: async (p) =>
                    dispatch(getPointInfoCollectionThunk(p)).unwrap(),

                deletePhotoFromStorage: async (p) =>
                    photoStorage.deletePhoto(p),

                updatePhotoStorage: async (photoIds) => {
                    const photosIdsForDelete = new Set<string>();
                    const photosIdsWithError = new Set<string>();

                    for (const photoId of photoIds) {
                        if (!photoId) {
                            continue;
                        }

                        const info = await photoStorage.getPhotoInfo(photoId);
                        if (!info) continue;

                        if (info.uploaded) {
                            photosIdsForDelete.add(photoId);
                        } else {
                            photosIdsWithError.add(photoId);
                        }
                    }

                    if (photosIdsWithError.size > 0) {
                        await Promise.allSettled(
                            [...photosIdsWithError].map((id) =>
                                photoStorage.updatePhotoInfo(id, {
                                    taskComplete: true,
                                }),
                            ),
                        );
                    }

                    if (photosIdsForDelete.size > 0) {
                        await photoStorage.deletePhotosByIds([
                            ...photosIdsForDelete,
                        ]);
                    }
                },

                putPointChangePhoto: async ({ photoId, buildingId }) => {
                    const base64 =
                        (await photoStorage.getPhoto(photoId)) ?? null;
                    const photoInfo = await photoStorage.getPhotoInfo(photoId);

                    if (!base64 || !photoInfo) {
                        throw new Error(
                            `Photo ${photoId} not found in storage`,
                        );
                    }

                    const normalizedBase64 = extractBase64Content(base64);

                    await patchBuildingChangePhotoBase64({
                        buildingId,
                        filename: photoInfo.name,
                        base64ContentPhoto: normalizedBase64,
                    });
                },

                updateCheckout: async () => {
                    const isManager = isManagerRole(role);

                    const routeId = isManager
                        ? activeSchedule?.routeId
                        : route?.uuid;

                    if (routeId) {
                        dispatch(
                            getCheckoutsThunk({
                                routeId,
                                strict: true,
                            }),
                        );
                    }
                },

                getTaskListsThunk: async () => {
                    const roleSlug = isManagerRole(role)
                        ? ROLE_CHILDREN_MAP[role]
                        : undefined;

                    await dispatch(
                        getTaskListsThunk({
                            targetIds: Object.keys(points),
                            roleSlug,
                        }),
                    ).unwrap();
                },
            }) satisfies CommandHandlers,
        [dispatch, role, activeSchedule?.routeId, route?.uuid, points],
    );
};
